import { agendaClock, AgendaStep } from './agenda-clock'
import {
	ActivityGroup,
	agendaOutline,
	AgendaPosition,
	ScriptPositionState,
} from './agenda-outline'
import { ConsoleState, RunningState } from './presentation-machine'

/**
 * What the console's presenter-mode components show, worked out from the
 * state machine and the script - pure, so the rules are tested without
 * rendering. Formatting that depends on the user (12- or 24-hour clocks,
 * the UI language) comes in as functions.
 */

export type PanelStatus =
	| 'opening'
	| 'windowed'
	| 'fullscreen'
	| 'black'
	| 'lost'
	| 'other-build'

export const panelStatus = (state: RunningState): PanelStatus => {
	switch (state.phase) {
		case 'opening':
			return 'opening'
		case 'incompatible':
			return 'other-build'
		case 'live':
			if (!state.present) {
				return 'lost'
			}
			return state.blank ? 'black' : state.display
	}
}

/** Which of its controls the Present button shows. */
export type PresentButtonState =
	/** Present (and, where it cannot place, Full screen here). */
	| 'idle'
	/** Show full screen and End: the presentation window is not full screen. */
	| 'windowed'
	/** End only. */
	| 'running'
	/** Exit full screen: one-screen presenting in this window. */
	| 'here'

export const presentButtonState = (state: ConsoleState): PresentButtonState => {
	switch (state.phase) {
		case 'idle':
		case 'guide':
			return 'idle'
		case 'here':
			return 'here'
		case 'opening':
			return 'windowed'
		case 'live':
			return state.display === 'fullscreen' ? 'running' : 'windowed'
		case 'incompatible':
			return 'running'
	}
}

export type Formats = {
	/** A clock time the user's way. */
	time: (date: Date) => string
	/** A day, short. */
	day: (date: Date) => string
	/** A duration in seconds, `m:ss` or `h:mm:ss`. */
	duration: (seconds: number) => string
}

export type PanelNowView = {
	title: string
	notes: string
	/** `09:00–09:30`, or the start alone without a planned end. */
	plan: string
	/** Against the plan; negative when over time. */
	minutesLeft?: number
	over: boolean
	activity?: AgendaStep['activity']
}

export type PanelRowView = {
	key: string
	/** Shown above the row when the agenda moves to another day. */
	day?: string
	at: string
	title: string
	position: AgendaPosition
	groups: ActivityGroup[]
}

export type PanelView = {
	status: PanelStatus
	screenLabel?: string
	clock: string
	elapsed: string
	now?: PanelNowView
	agenda: PanelRowView[]
}

type PanelInput = {
	state: RunningState
	script: ScriptPositionState
	current: AgendaStep | undefined
	now: Date
	format: Formats
}

export const panelView = ({
	state,
	script,
	current,
	now,
	format,
}: PanelInput): PanelView => {
	const step = agendaClock(current, now).now
	const rows = agendaOutline(script)
	const severalDays = script.chunks.length > 1
	return {
		status: panelStatus(state),
		screenLabel: state.phase === 'live' ? state.screenLabel : undefined,
		clock: format.time(now),
		elapsed: format.duration(
			Math.max(0, Math.floor((now.getTime() - state.startedAt) / 1000))
		),
		now: step && {
			title: step.title,
			notes: step.notes,
			plan: step.plannedEnd
				? `${format.time(step.plannedStart)}–${format.time(step.plannedEnd)}`
				: format.time(step.plannedStart),
			minutesLeft: step.minutesLeft,
			over: step.minutesLeft !== undefined && step.minutesLeft < 0,
			activity: step.activity,
		},
		agenda: rows.map((row, index) => ({
			key: row.key,
			day:
				severalDays &&
				(index === 0 || rows[index - 1].chunkIndex !== row.chunkIndex)
					? format.day(script.chunks[row.chunkIndex].startDate)
					: undefined,
			at: format.time(row.plannedStart),
			title: row.title,
			position: row.position,
			groups: row.groups,
		})),
	}
}
